import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { SiteProvider } from "@/context/SiteContext";
import { LocaleProvider, langFromPath, localizePath } from "@/i18n";
import TranslationsAdmin from "@/pages/admin/Translations";
import CalendarsAdmin from "@/pages/admin/Calendars";
import DocumentsAdmin from "@/pages/admin/Documents";
import BookingAdmin from "@/pages/admin/Bookings";
import PricingAdmin from "@/pages/admin/Pricing";
import Booking, { BookingConfirmation, BookingPayment } from "@/pages/public/Booking";
import { ContractSign, LeaveReview } from "@/pages/public/GuestStay";
import PublicLayout from "@/components/public/Layout";
import Home from "@/pages/public/Home";
import Apartments from "@/pages/public/Apartments";
import ApartmentDetail from "@/pages/public/ApartmentDetail";
import GroupStay from "@/pages/public/GroupStay";
import Queyras from "@/pages/public/Queyras";
import Gallery from "@/pages/public/Gallery";
import Reviews from "@/pages/public/Reviews";
import Practical from "@/pages/public/Practical";
import Contact from "@/pages/public/Contact";
import Availability from "@/pages/public/Availability";
import Legal from "@/pages/public/Legal";
import AdminLayout from "@/components/admin/AdminLayout";
import { Login, Forgot, Reset } from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Requests from "@/pages/admin/Requests";
import SingletonEditor from "@/pages/admin/Content";
import Account from "@/pages/admin/Account";
import { ApartmentsAdmin, OptionsAdmin, MediaAdmin, ReviewsAdmin, FaqAdmin, SeoAdmin, QueyrasAdmin } from "@/pages/admin/Resources";

// URL inconnue : retour à l'accueil dans la langue courante (/en, /de, /it ou /)
function LangHomeRedirect() {
  const { pathname } = useLocation();
  return <Navigate to={localizePath("/", langFromPath(pathname))} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <LocaleProvider>
      <SiteProvider>
        <Toaster position="top-center" richColors toastOptions={{ style: { fontFamily: "inherit" } }} />
        <Routes>
          {/* Site public : FR sans préfixe, EN/DE/IT sous /en, /de, /it */}
          {["", "/en", "/de", "/it"].map((prefix) => (
            <Route key={prefix || "fr"} path={prefix || "/"} element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="appartements" element={<Apartments />} />
              <Route path="appartements/:slug" element={<ApartmentDetail />} />
              {!prefix && <Route path="apercu/appartements/:id" element={<ApartmentDetail preview />} />}
              <Route path="sejour-en-groupe" element={<GroupStay />} />
              <Route path="decouvrir-le-queyras" element={<Queyras />} />
              <Route path="decouvrir-le-queyras/:section" element={<Queyras />} />
              <Route path="galerie" element={<Gallery />} />
              <Route path="reservation" element={<Booking />} />
              <Route path="reservation/confirmation" element={<BookingConfirmation />} />
              <Route path="reservation/paiement" element={<BookingPayment />} />
              <Route path="reservation/contrat" element={<ContractSign />} />
              <Route path="avis-voyageurs/laisser-un-avis" element={<LeaveReview />} />
              <Route path="avis-voyageurs" element={<Reviews />} />
              <Route path="infos-pratiques" element={<Practical />} />
              <Route path="contact" element={<Contact />} />
              <Route path="demande-de-disponibilite" element={<Availability />} />
              <Route path="mentions-legales" element={<Legal kind="legal" />} />
              <Route path="politique-de-confidentialite" element={<Legal kind="privacy" />} />
            </Route>
          ))}
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/mot-de-passe-oublie" element={<Forgot />} />
          <Route path="/admin/reinitialiser/:token" element={<Reset />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="appartements" element={<ApartmentsAdmin />} />
            <Route path="options" element={<OptionsAdmin />} />
            <Route path="galerie" element={<MediaAdmin />} />
            <Route path="avis" element={<ReviewsAdmin />} />
            <Route path="queyras" element={<QueyrasAdmin />} />
            <Route path="faq" element={<FaqAdmin />} />
            <Route path="demandes" element={<Requests />} />
            <Route path="contenus" element={<SingletonEditor key="content" kind="content" />} />
            <Route path="parametres" element={<SingletonEditor key="settings" kind="settings" />} />
            <Route path="seo" element={<SeoAdmin />} />
            <Route path="traductions" element={<TranslationsAdmin />} />
            <Route path="calendriers" element={<CalendarsAdmin />} />
            <Route path="documents" element={<DocumentsAdmin />} />
            <Route path="reservations" element={<BookingAdmin />} />
            <Route path="tarifs-sejour" element={<PricingAdmin />} />
            <Route path="compte" element={<Account />} />
          </Route>
          <Route path="*" element={<LangHomeRedirect />} />
        </Routes>
      </SiteProvider>
      </LocaleProvider>
    </BrowserRouter>
  );
}

export default App;
